/**
 * Plugin-calendar graceful-degrade notice (#242, slice C1, epic #228;
 * `06-timeline.md` §3).
 *
 * A plugin-mode calendar (`CalendarDefinition.mode === 'plugin'`) LOADS — its
 * pack's entities / traits / types stay available — but the v1 structured
 * engine cannot decompose it into cycles (the plugin RUNTIME is Phase 3, out
 * of this slice). So calendar-dependent surfaces (the date picker, date
 * formatting) must render this clear notice instead of failing: no crash, no
 * broken render. **No plugin code executes** to produce it — it is a static,
 * deterministic banner keyed only on `isPluginCalendar`.
 *
 * Kept as a tiny self-contained surface (not a `@dl/ui` atom) so the client
 * owns the degrade copy + a11y affordances and a unit test can assert the
 * notice renders for a plugin calendar without standing up the whole app.
 */

import { div, text, type Mountable } from '@llui/dom';

import { t } from '../i18n.js';

/** Stable test handle for the degrade notice. */
export const CALENDAR_DEGRADE_NOTICE_TESTID = 'calendar-plugin-unsupported';

/**
 * The headline degrade string ("This calendar is not fully supported yet").
 * A pure, locale-aware read — split out so a non-DOM unit test can assert the
 * copy without rendering, and so any surface can reuse it inline (e.g. the
 * calendar-settings list `summary`).
 */
export function pluginCalendarNoticeText(): string {
  return t('calendar.plugin_unsupported');
}

/** The supporting detail line (what the GM can still do). */
export function pluginCalendarNoticeDetail(): string {
  return t('calendar.plugin_unsupported.detail');
}

/**
 * Render the "calendar not fully supported" notice for a plugin-mode
 * calendar (#242). A `role="status"` banner pairing the headline with a
 * detail line — the non-color cue (the WORDS) carries the signal, per the
 * a11y invariant. Deterministic; runs no plugin code.
 */
export function calendarDegradeNotice(): Mountable {
  return div(
    {
      class: 'calendar-plugin-notice',
      'data-testid': CALENDAR_DEGRADE_NOTICE_TESTID,
      role: 'status',
    },
    [
      div({ class: 'calendar-plugin-notice-title' }, [text(pluginCalendarNoticeText())]),
      div({ class: 'calendar-plugin-notice-detail' }, [text(pluginCalendarNoticeDetail())]),
    ],
  );
}
